import Reveal from "@/components/Reveal";
import { Link } from "react-router-dom";

export default function Approche() {
    return (
        <div data-testid="approche-page">
            {/* INTRO */}
            <section className="max-w-7xl mx-auto px-6 md:px-12 pt-20 pb-24">
                <Reveal>
                    <span className="overline">Notre approche</span>
                    <h1 className="mt-6 font-serif text-6xl md:text-7xl lg:text-8xl font-light text-brand-ink leading-[1.02] max-w-5xl">
                        Une rénovation
                        <span className="italic block">pensée comme une œuvre.</span>
                    </h1>
                </Reveal>
            </section>

            {/* ÉDITORIAL — citation + paragraphes longs */}
            <section className="bg-brand-bgalt py-28 md:py-36">
                <div className="max-w-5xl mx-auto px-6 md:px-12">
                    <Reveal>
                        <p className="font-serif text-3xl md:text-4xl italic font-light text-brand-ink leading-[1.3]">
                            Avant de poser un mur, nous écoutons le lieu. Ses proportions, sa lumière, ses silences.
                            Nous écoutons aussi celles et ceux qui l'habiteront : leurs gestes, leurs usages, ce qu'ils
                            attendent quand ils ouvriront la porte le matin.
                        </p>
                    </Reveal>
                    <div className="mt-16 grid md:grid-cols-2 gap-12 text-lg leading-relaxed text-brand-sageDark font-light">
                        <Reveal delay={80}>
                            <p>
                                Japet Agencement est une société de rénovation française entièrement dédiée aux
                                projets haut de gamme. Notre équipe coordonne l'ensemble des corps d'état nécessaires
                                à la transformation d'un appartement ou d'une maison de prestige — maçonnerie,
                                plâtrerie, électricité, plomberie, menuiserie, ébénisterie, peinture, finitions.
                            </p>
                        </Reveal>
                        <Reveal delay={160}>
                            <p>
                                Nous intervenons aux côtés d'architectes, de décorateurs ou directement pour le maître
                                d'ouvrage. Toutes nos interventions sont couvertes par une assurance responsabilité
                                civile professionnelle et la garantie décennale.
                            </p>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* SECTION VISUELLE + TEXTE */}
            <section className="py-28 md:py-36 max-w-7xl mx-auto px-6 md:px-12">
                <div className="grid md:grid-cols-12 gap-12 md:gap-16 items-center">
                    <div className="md:col-span-7 hover-zoom">
                        <img
                            src="https://images.unsplash.com/photo-1663811396760-b6c84fa45ee9?auto=format&fit=crop&w=1400&q=80"
                            alt="Détail d'un intérieur rénové"
                            className="w-full h-[640px] object-cover"
                        />
                    </div>
                    <div className="md:col-span-4 md:col-start-9">
                        <span className="overline">Ce qui nous distingue</span>
                        <h2 className="mt-5 font-serif text-4xl md:text-5xl font-light text-brand-ink leading-tight">
                            Un seul
                            <span className="italic"> interlocuteur</span>,
                            tout le chantier.
                        </h2>
                        <p className="mt-8 text-lg text-brand-sageDark font-light leading-relaxed">
                            Pas de transferts de dossiers, pas d'intermédiaires invisibles. Une personne suit votre
                            chantier de la signature à la livraison — c'est elle qui pilote les corps d'état,
                            commande les matériaux et garde la main sur chaque échéance.
                        </p>
                    </div>
                </div>
            </section>

            {/* ENGAGEMENTS — liste éditoriale verticale */}
            <section className="bg-brand-ink text-brand-bg py-28 md:py-36">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="max-w-3xl">
                        <span className="overline" style={{ color: "#A8C0B0" }}>Nos engagements</span>
                        <h2 className="mt-6 font-serif text-5xl md:text-6xl font-light leading-tight">
                            Trois promesses,
                            <span className="italic block text-brand-sage">tenues à chaque chantier.</span>
                        </h2>
                    </div>

                    <div className="mt-20 space-y-12 md:space-y-16">
                        {[
                            {
                                k: "Devis",
                                title: "Transparence intégrale du devis",
                                text: "Chiffrage poste par poste, sans poste « divers », sans surprise au cours du chantier. Si une décision impacte le budget, vous êtes prévenu avant — pas après.",
                            },
                            {
                                k: "Délais",
                                title: "Engagement contractuel sur les délais",
                                text: "Le calendrier est planifié avant le premier jour de chantier et tenu jusqu'à la livraison. Les retards sont l'exception, jamais la règle.",
                            },
                            {
                                k: "Finitions",
                                title: "Qualité d'exécution non négociable",
                                text: "Contrôle visuel à chaque jalon, reprise de tout détail qui ne nous satisferait pas — avant même que vous ne le voyiez.",
                            },
                        ].map((eng, idx) => (
                            <Reveal key={eng.k} delay={idx * 80}>
                                <div className="grid md:grid-cols-12 gap-8 items-start border-t border-brand-bg/15 pt-10">
                                    <div className="md:col-span-3">
                                        <p className="font-serif text-2xl italic text-brand-sage">— {eng.k}</p>
                                    </div>
                                    <div className="md:col-span-9">
                                        <h3 className="font-serif text-3xl md:text-4xl font-light leading-tight">{eng.title}</h3>
                                        <p className="mt-5 text-lg text-brand-bg/75 font-light leading-relaxed max-w-2xl">{eng.text}</p>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-24 text-center max-w-3xl mx-auto px-6">
                <Reveal>
                    <h2 className="font-serif text-4xl md:text-5xl font-light text-brand-ink leading-tight">
                        Vous voulez en discuter
                        <span className="italic"> de vive voix </span>?
                    </h2>
                    <Link
                        to="/devis"
                        data-testid="approche-cta"
                        className="mt-10 inline-flex items-center gap-3 px-9 py-5 bg-brand-ink hover:bg-brand-sage text-brand-bg text-sm uppercase tracking-[0.22em] transition-colors"
                    >
                        Échanger sur votre projet
                    </Link>
                </Reveal>
            </section>
        </div>
    );
}
